import {
  CREATE_COPILOT_NOTIFICATION_MUTATION,
  SET_COPILOT_NOTIFICATION_FEEDBACK_MUTATION,
} from '@coach-bot/data-access/copilot-scripts/mutations';
import { GraphQLClientService } from '@coach-bot/data-access/shared/services/graphql-client.service';
import { ExtensionCommunicationService, ServiceWorkerCommunicationService, safeSendChromeRuntimeMessage, withChromeRuntime } from '@shared/extension';
import { BackgroundAction, DashboardAction, InjectedScriptAction, WindowEventType } from '@shared/extension/enums';
import { RequestResponseEvent } from '@shared/http/interfaces';
import { getHttpVersion } from '@shared/http/utils/get-http-version';
import { SentryEventType } from '@shared/sentry';
import gql from 'graphql-tag';
import { ContentScriptStore } from '../store';
import { postToBackend, sendLatestDashboardMessage } from '../utils';

type EventHandler = (event: MessageEvent, store?: ContentScriptStore) => void;

const MESSAGE_MANDLERS: { [key: string]: EventHandler } = {
  [WindowEventType.FromDashboard]: handleFromDashboardEvent,
  [WindowEventType.FromInjectedScript]: handleFromInjectedScriptEvent,
  [WindowEventType.FromPage]: handleFromPageEvent,
  [WindowEventType.FromPageEventData]: handleFromPageEventDataEvent,
  [WindowEventType.Graph]: handleGraphEvent,
  [SentryEventType.PostBackend]: handlePostBackendEvent,
};

let extensionCommunicationService: ExtensionCommunicationService;
let serviceWorkerCommunicationService: ServiceWorkerCommunicationService;

export function startHandlingWindowMessages(
  extensionCommunicationServiceInstance: ExtensionCommunicationService,
  serviceWorkerCommunicationServiceInstance: ServiceWorkerCommunicationService
): void {
  extensionCommunicationService = extensionCommunicationServiceInstance;
  serviceWorkerCommunicationService = serviceWorkerCommunicationServiceInstance;
  window.addEventListener('message', onMessage);
}

export function removeWindowMessagesHandler(): void {
  window.removeEventListener('message', onMessage);
}

function onMessage(event: MessageEvent): void {
  // We only accept messages from ourselves
  if (event.source != window || !event.data.type) {
    return;
  }
  const store = ContentScriptStore.getInstance();
  const eventHandler: EventHandler | undefined = MESSAGE_MANDLERS[event.data.type];
  if (eventHandler) {
    eventHandler(event, store);
  }
}

// Handlers

function handleFromPageEvent(event: MessageEvent): void {
  window.postMessage(
    { type: WindowEventType.FromContentScriptBridgeData, text: JSON.stringify(event.data.text) },
    window.location.origin
  );
}

function handleFromPageEventDataEvent(event: MessageEvent, store: ContentScriptStore): void {
  postToBackend({
    pageUrl: window.location.href,
    request: {
      httpVersion: getHttpVersion(),
      url: window.location.href,
    },
    response: {
      body: JSON.stringify(event.data.text),
      contentType: 'text/html',
      dateTime: new Date().toISOString(),
    },
    app: store.learningApp$.value,
    eventType: event.data.eventType,
    event: event.data.event,
  });
}

async function handleFromInjectedScriptEvent(event: MessageEvent): Promise<void> {
  const { action, detail } = event.data;
  switch (action) {
    case InjectedScriptAction.GetExtensionSignedInStatus: {
      withChromeRuntime(() => {
        safeSendChromeRuntimeMessage({ action: BackgroundAction.GetExtensionSignedInStatus, detail }, response => {
          window.postMessage(
            {
              type: WindowEventType.FromContentScript,
              action: InjectedScriptAction.ExtensionSignedInStatusRetrieved,
              response: response.isSignedIn,
            },
            '*'
          );
        });
      });
      return;
    }
    case InjectedScriptAction.GetThirdPartyAppCredentials: {
      withChromeRuntime(() => {
        safeSendChromeRuntimeMessage({ action: BackgroundAction.GetThirdPartyAppCredentials, detail }, response => {
          window.postMessage(
            {
              type: WindowEventType.FromContentScript,
              action: InjectedScriptAction.ThirdPartyAppCredentialsRetrieved,
              response: response.credentials,
            },
            '*'
          );
        });
      });
      return;
    }
    case InjectedScriptAction.GetThirdPartyAppCredentialsPopupDetails: {
      withChromeRuntime(() => {
        safeSendChromeRuntimeMessage({ action: BackgroundAction.GetThirdPartyAppCredentialsPopupDetails, detail }, response => {
          window.postMessage(
            {
              type: WindowEventType.FromContentScript,
              action: InjectedScriptAction.ThirdPartyAppCredentialsPopupDetailsRetrieved,
              response: response.popupDetails,
            },
            '*'
          );
        });
      });
      return;
    }
    case InjectedScriptAction.OpenThirdPartyAppCredentialsPopup: {
      // Handled by third-party-app-credentials-popup-script
      return;
    }
    default: {
      withChromeRuntime(() => {
        safeSendChromeRuntimeMessage({ type: WindowEventType.FromContentScript, id: event.data.id, action }, response => {
          window.postMessage({ type: WindowEventType.FromBackgroundScript, id: event.data.id, response }, '*');
        });
      });
      return;
    }
  }
}

function handleGraphEvent(event: MessageEvent, store: ContentScriptStore): void {
  const graphQLClientService = GraphQLClientService.getInstance();
  if (event.data.action === InjectedScriptAction.CreateUINotification) {
    graphQLClientService
      .subscribe(CREATE_COPILOT_NOTIFICATION_MUTATION, {
        studentEmail: store.profileEmail$.value,
      })
      .subscribe(res => {
        window.postMessage(
          {
            type: WindowEventType.FromBackgroundScript,
            action: InjectedScriptAction.CreateUINotification,
            response: res.data.createCopilotNotification,
          },
          '*'
        );
      });
  } else if (event.data.action === InjectedScriptAction.SetFeedback) {
    graphQLClientService
      .subscribe(gql(SET_COPILOT_NOTIFICATION_FEEDBACK_MUTATION), {
        notificationId: event.data.info.id,
        studentFeedback: event.data.info.studentFeedback,
        studentFeedbackNotes: event.data.info.studentFeedbackNotes,
      })
      .subscribe(res => {
        window.postMessage(
          {
            type: WindowEventType.FromBackgroundScript,
            action: InjectedScriptAction.SetFeedback,
            response: res.data.setCopilotNotificationFeedback,
          },
          '*'
        );
      });
  }
}

function handlePostBackendEvent(event: MessageEvent, store: ContentScriptStore): void {
  postToBackend({
    ...(event.data.content as RequestResponseEvent),
    app: store.learningApp$.value,
  });
}

function handleFromDashboardEvent(event: MessageEvent): void {
  const { action, detail } = event.data;
  switch (action) {
    case DashboardAction.IsExtensionInstalled: {
      window.postMessage({ type: WindowEventType.FromDashboard, action: DashboardAction.IsExtensionInstalledResponse }, '*');
      return;
    }
    case DashboardAction.DashboardReady: {
      sendLatestDashboardMessage(serviceWorkerCommunicationService);
      return;
    };
    case DashboardAction.DashboardSignedIn: {
      extensionCommunicationService.signIn(detail.username, detail.password);
      return;
    }
    case DashboardAction.DashboardSignedOut: {
      extensionCommunicationService.signOut();
      return;
    }
  }
}
